/**
 * useFocusTrap — keyboard-navigation trap для модалок и панелей (inbox-459).
 *
 * При активации:
 *  - сохраняет previously-focused элемент;
 *  - ставит фокус на первый focusable внутри ref'а (или на сам контейнер);
 *  - перехватывает Tab/Shift+Tab — цикл внутри контейнера;
 *  - Esc → колбэк onEscape (опционально);
 *  - при деактивации возвращает фокус на сохранённый элемент.
 *
 * Использование:
 *   <UiModal>
 *     <div ref="root">…</div>
 *   </UiModal>
 *
 *   const root = ref<HTMLElement | null>(null);
 *   useFocusTrap(root, { onEscape: () => emit('close') });
 *
 * Селекторы focusable — общий набор: a[href], button, input, textarea,
 * select, [tabindex]:not([tabindex="-1"]). Disabled / aria-hidden
 * пропускаются.
 */

import { ref, watch, type Ref } from 'vue';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(root: HTMLElement): HTMLElement[] {
  const els = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  return els.filter((el) => {
    if (el.hasAttribute('aria-hidden')) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
}

interface Options {
  /** Колбэк при нажатии Esc. Если не задан — Esc игнорируется. */
  onEscape?: () => void;
  /** Включён по умолчанию. Реактивный ref для on/off (например watch isOpen). */
  active?: Ref<boolean>;
  /** Не ставить autofocus при активации (если родитель сам решает). */
  skipAutofocus?: boolean;
}

export function useFocusTrap(
  containerRef: Ref<HTMLElement | null>,
  opts: Options = {},
) {
  const active = opts.active ?? ref(true);
  let previouslyFocused: HTMLElement | null = null;

  function handleKeydown(e: KeyboardEvent) {
    const root = containerRef.value;
    if (!root) return;

    if (e.key === 'Escape' && opts.onEscape) {
      e.stopPropagation();
      opts.onEscape();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusable = getFocusable(root);
    if (focusable.length === 0) {
      e.preventDefault();
      root.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const current = document.activeElement as HTMLElement | null;

    if (e.shiftKey) {
      if (current === first || !root.contains(current)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (current === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function activate() {
    const root = containerRef.value;
    if (!root) return;
    previouslyFocused = document.activeElement as HTMLElement | null;
    document.addEventListener('keydown', handleKeydown);
    if (!opts.skipAutofocus) {
      requestAnimationFrame(() => {
        const focusable = getFocusable(root);
        if (focusable.length > 0) {
          focusable[0].focus();
        } else {
          root.setAttribute('tabindex', '-1');
          root.focus();
        }
      });
    }
  }

  function deactivate() {
    document.removeEventListener('keydown', handleKeydown);
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      try {
        previouslyFocused.focus();
      } catch {
        /* element may have been unmounted — ignore */
      }
    }
    previouslyFocused = null;
  }

  watch(
    active,
    (isActive) => {
      if (isActive) activate();
      else deactivate();
    },
    { immediate: true, flush: 'post' },
  );

  return { activate, deactivate };
}
